import { useTranslation } from 'react-i18next';
import { AlertTriangle, CreditCard, MessageCircle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';
import { toast } from 'sonner';

const SUPPORT_WHATSAPP = (import.meta.env.VITE_SUPPORT_WHATSAPP as string | undefined) ?? '';
const SUPPORT_EMAIL = (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? 'help@careos.life';

function buildSupportUrl() {
  const wa = SUPPORT_WHATSAPP.replace(/[^\d+]/g, '').replace(/^\+/, '');
  if (wa) return `https://wa.me/${wa}`;
  return `mailto:${SUPPORT_EMAIL}`;
}

export function PaymentIssueBanner() {
  const { t, i18n } = useTranslation();
  const { subscription, session } = useAuth();
  const [busy, setBusy] = useState(false);

  if (subscription.paymentStatus === 'ok') return null;

  const isExpired = subscription.paymentStatus === 'past_due_expired';
  const graceEnd = subscription.gracePeriodEnd ? new Date(subscription.gracePeriodEnd) : null;
  const formattedDate = graceEnd
    ? new Intl.DateTimeFormat(i18n.language || 'pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      }).format(graceEnd)
    : '';

  const remainingMs = graceEnd ? graceEnd.getTime() - Date.now() : 0;
  const remainingHours = Math.max(0, Math.ceil(remainingMs / (60 * 60 * 1000)));
  const remainingDays = Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));
  const remainingLabel =
    remainingHours <= 24
      ? t('subscription.payment.grace.remainingHours')
      : t('subscription.payment.grace.remainingDays', { count: remainingDays });

  const handlePortal = async () => {
    if (!session?.access_token) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error || !data?.url) throw error ?? new Error('no_portal_url');
      window.open(data.url as string, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(t('subscription.errors.portalFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Alert variant={isExpired ? 'destructive' : 'default'} className={isExpired ? '' : 'border-warning/40 bg-warning/10'}>
      <AlertTriangle className="h-5 w-5" />
      <AlertTitle>
        {isExpired
          ? t('subscription.payment.expired.title')
          : t('subscription.payment.grace.title')}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          {isExpired
            ? t('subscription.payment.expired.body')
            : t('subscription.payment.grace.body', { date: formattedDate })}
        </p>
        {!isExpired && (
          <p className="text-sm font-medium">{remainingLabel}</p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" onClick={handlePortal} disabled={busy}>
            <CreditCard className="mr-2 h-4 w-4" />
            {t('subscription.payment.cta.updatePayment')}
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={buildSupportUrl()} target="_blank" rel="noopener noreferrer">
              <MessageCircle className="mr-2 h-4 w-4" />
              {t('subscription.payment.cta.contactSupport')}
            </a>
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
